import {
  Component,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  NgZone,
} from '@angular/core';
import type { HeatmapCellPayload } from '../details-heatmap-widget/details-heatmap-widget.component';

export interface HeatmapFeedbackItem {
  sentiment: string;
  text: string;
  /** When present, opens feed detail panel (from raw_comments / API `feed_id`). */
  feedId?: string;
  /**
   * One row per configured cohort dimension (same as heatmap cohort options), excluding `rating_bucket`.
   * Used in the Customer Feedback footer.
   */
  cohortTagsFooter: Array<{ label: string; value: string }>;
}

/** Cohort dimension option: `value` is `field_id` (must match insights `heatmap_v2.cohorts`). */
export interface HeatmapCohortFilterOption {
  value: string;
  label: string;
}

@Component({
  selector: 'app-sentiment-details-heatmap-analysis',
  standalone: false,
  templateUrl: './details-heatmap-analysis.component.html',
  styleUrls: ['./details-heatmap-analysis.component.scss'],
})
export class SentimentDetailsHeatmapAnalysisComponent implements AfterViewInit, OnDestroy, OnChanges {
  /** Legend + gap + heatmap (Color Intensity card + Heatmap card). */
  @ViewChild('leftStackMeasureRoot') leftStackMeasureRoot?: ElementRef<HTMLElement>;
  @ViewChild('feedbackCardRoot') feedbackCardRoot?: ElementRef<HTMLElement>;

  /**
   * Fixed height so Customer Feedback card outer height === left stack (Color Intensity + Heatmap).
   */
  feedbackCardHeightPx: number | undefined;

  @Input() levelFilter = 'Themes';
  @Input() themeFilterLabel = 'All Themes';
  @Input() themeFilterValue: string | null = null;
  /** Selected cohort option `value` (must match an entry in `cohortFilterOptions`). */
  @Input() cohortFilterValue = '';
  @Input() cohortFilterOptions: HeatmapCohortFilterOption[] = [];
  @Input() sentimentMode: 'negative' | 'non_negative' = 'negative';
  @Input() heatmapData: any[] = [];
  @Input() themes: Array<{ themeId: string; themeName: string }> = [];
  @Input() selectedCell: HeatmapCellPayload | null = null;
  @Input() feedbackList: HeatmapFeedbackItem[] = [];
  @Input() feedbackLoading = false;
  @Input() feedbackLoadingMore = false;
  @Input() feedbackHasMore = false;
  @Input() showSelectThemeMessage = false;
  /** `module_configuration_v2.sentiment_analysis.sub_modules.feed_link_redirect` */
  @Input() showFeedLinkRedirect = true;
  /** `module_configuration_v2.sentiment_analysis.sub_modules.cohort_footer` */
  @Input() showCohortFooter = true;
  @Output() levelFilterChange = new EventEmitter<string>();
  @Output() themeFilterChange = new EventEmitter<string>();
  @Output() cohortFilterChange = new EventEmitter<string>();
  @Output() sentimentModeChange = new EventEmitter<'negative' | 'non_negative'>();
  @Output() backToThemes = new EventEmitter<void>();
  @Output() cellClick = new EventEmitter<HeatmapCellPayload>();
  @Output() feedbackLoadMore = new EventEmitter<void>();
  @Output() openFeedbackDetails = new EventEmitter<string>();

  private resizeObserver: ResizeObserver | null = null;
  private measureRaf = 0;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly ngZone: NgZone,
  ) {}

  ngAfterViewInit(): void {
    this.attachResizeObserver();
    this.scheduleSyncHeights();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['showSelectThemeMessage'] ||
      changes['heatmapData'] ||
      changes['sentimentMode']
    ) {
      this.scheduleSyncHeights();
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    if (this.measureRaf) {
      cancelAnimationFrame(this.measureRaf);
      this.measureRaf = 0;
    }
  }

  setSentimentMode(mode: 'negative' | 'non_negative'): void {
    this.sentimentModeChange.emit(mode);
  }

  onBackToThemes(): void {
    this.backToThemes.emit();
  }

  onFeedbackPlaceholderScroll(event: Event): void {
    if (!this.feedbackHasMore || this.feedbackLoading || this.feedbackLoadingMore) {
      return;
    }
    const el = event.target as HTMLElement;
    if (!el) {
      return;
    }
    if (el.scrollHeight - el.scrollTop - el.clientHeight > 100) {
      return;
    }
    this.feedbackLoadMore.emit();
  }

  get volumeWeightLegend(): string {
    return this.sentimentMode === 'negative'
      ? 'Number of negative responses relative to the highest cell count.'
      : 'Number of non-negative (positive + neutral) responses relative to the highest cell count.';
  }

  private attachResizeObserver(): void {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    this.resizeObserver = new ResizeObserver(() => this.scheduleSyncHeights());
  }

  private scheduleSyncHeights(): void {
    if (this.measureRaf) {
      cancelAnimationFrame(this.measureRaf);
    }
    this.measureRaf = requestAnimationFrame(() => {
      this.measureRaf = 0;
      this.ngZone.run(() => {
        this.syncResizeObserverTarget();
        this.applyFeedbackBodyMinHeight();
      });
    });
  }

  private syncResizeObserverTarget(): void {
    if (!this.resizeObserver) {
      return;
    }
    this.resizeObserver.disconnect();
    const el = this.leftStackMeasureRoot?.nativeElement;
    if (el) {
      this.resizeObserver.observe(el);
    }
  }

  private applyFeedbackBodyMinHeight(): void {
    const leftStackEl = this.leftStackMeasureRoot?.nativeElement;
    const cardEl = this.feedbackCardRoot?.nativeElement;
    if (!leftStackEl || !cardEl) {
      return;
    }
    const leftStackH = Math.round(leftStackEl.getBoundingClientRect().height);
    /** Guard against tiny measurements during initial render. */
    const next = Math.max(leftStackH, 240);
    if (this.feedbackCardHeightPx !== next) {
      this.feedbackCardHeightPx = next;
      this.cdr.detectChanges();
    }
  }
}
